import React from 'react';
import { C, S } from '../../utils/styles';
import { fmt } from '../../utils/formatters';
import Icon from '../Icon';
import { IBOS_STATUSES } from '../../data/ibosDefaults';

export default function IBOSLayerCard({ layer, expanded, onToggleExpand, onToggleEnabled, onInputChange }) {
  const statusInfo = IBOS_STATUSES[layer.effectiveStatus] || IBOS_STATUSES.na;
  const statusColor = C[statusInfo.color] || C.textLight;
  const isToggleable = layer.effectiveStatus === 'active' || layer.effectiveStatus === 'available';
  const inputs = layer.inputs || {};

  return (
    <div
      data-testid={`ibos-layer-card-${layer.id}`}
      style={{
        border: `1px solid ${C.border}`,
        borderRadius: 10,
        background: C.card,
        overflow: 'hidden',
        transition: 'border-color 150ms ease',
        borderColor: expanded ? statusColor : C.border,
      }}
    >
      {/* Header */}
      <button
        type="button"
        onClick={onToggleExpand}
        style={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          padding: '14px 16px',
          background: 'transparent',
          border: 'none',
          cursor: 'pointer',
          textAlign: 'left',
        }}
      >
        <div style={{ width: 32, height: 32, borderRadius: 8, background: `${statusColor}20`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Icon name={layer.icon || 'cube-outline'} style={{ fontSize: 18, color: statusColor }} />
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: C.text, marginBottom: 2 }}>
            Layer {layer.order}: {layer.name}
          </div>
          <div style={{ fontSize: 11, color: C.textLight }}>
            {layer.shortDescription}
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: 9, color: C.textLight, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Total Value</div>
            <div style={{ ...S.mono, fontSize: 14, fontWeight: 800, color: statusColor }}>
              {fmt((layer.result.employerSavings || 0) + (layer.result.employeeValue || 0))}
            </div>
          </div>
          <span style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 4,
            padding: '4px 10px',
            borderRadius: 6,
            background: `${statusColor}20`,
            color: statusColor,
            fontSize: 10,
            fontWeight: 700,
          }}>
            <Icon name={statusInfo.icon} style={{ fontSize: 12 }} />
            {statusInfo.label}
          </span>
          <Icon name={expanded ? 'chevron-up' : 'chevron-down'} style={{ fontSize: 16, color: C.textMid }} />
        </div>
      </button>

      {/* Expanded content */}
      {expanded && (
        <div style={{ padding: '0 16px 16px', borderTop: `1px solid ${C.borderLight}` }}>
          <p style={{ fontSize: 12, color: C.textMid, lineHeight: 1.6, margin: '12px 0 14px' }}>
            {layer.longDescription}
          </p>

          {/* Modeling inputs (only for configurable layers) */}
          {layer.calculationMethod !== 'computed_from_section125' && (
            <div style={{ background: C.bg, padding: 12, borderRadius: 8, marginBottom: 12 }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: C.textLight, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 10 }}>
                Modeling Inputs
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 10 }}>
                {Object.entries(inputs).map(([k, v]) => {
                  if (typeof v !== 'number') return null;
                  return (
                    <div key={k}>
                      <label style={{ ...S.label }}>{prettyInputLabel(k)}</label>
                      <input
                        type="number"
                        step="any"
                        value={v}
                        onChange={(e) => onInputChange?.(k, parseFloat(e.target.value) || 0)}
                        disabled={!isToggleable}
                        style={{ ...S.input, fontSize: 12 }}
                        data-testid={`ibos-input-${layer.id}-${k}`}
                      />
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Projected impact */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10, marginBottom: 12 }}>
            <ImpactBox label="Employer Savings" value={layer.result.employerSavings} color={C.blue} bg={C.blueLight} />
            <ImpactBox label="Employee Value" value={layer.result.employeeValue} color={C.green} bg={C.greenLight} />
            <ImpactBox label="Healthcare Impact" value={layer.result.healthcareImpact} color={C.purple} bg={C.purpleLight} />
          </div>

          {/* Compliance note */}
          {layer.complianceNote && (
            <div style={{ padding: 10, background: C.orangeLight, borderRadius: 8, display: 'flex', gap: 8, alignItems: 'flex-start', marginBottom: 12 }}>
              <Icon name="alert-circle-outline" style={{ fontSize: 14, color: C.orange, marginTop: 2 }} />
              <div style={{ fontSize: 11, color: C.textMid, lineHeight: 1.5 }}>
                <strong style={{ color: C.orange, marginRight: 4 }}>Compliance:</strong>
                {layer.complianceNote}
              </div>
            </div>
          )}

          {/* Covers */}
          {Array.isArray(layer.bulletPoints) && layer.bulletPoints.length > 0 && (
            <div>
              <div style={{ fontSize: 10, fontWeight: 700, color: C.textLight, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 6 }}>
                Covers
              </div>
              <ul style={{ margin: 0, paddingLeft: 20, color: C.textMid, fontSize: 12, lineHeight: 1.6 }}>
                {layer.bulletPoints.map((b, i) => (
                  <li key={i}>{b}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function prettyInputLabel(key) {
  const map = {
    monthlyContribution: 'Monthly Contribution ($)',
    visitsPerEmployeePerYear: 'Visits / Employee / Year',
    avoidedErVisitsPerYear: 'Avoided ER Visits / Year',
    avgErCost: 'Avg ER Cost ($)',
    perceivedValuePerMonth: 'Perceived Value / Month ($)',
    perEnrolledAnnualValue: 'Per-Enrolled Annual Value ($)',
    avgAnnualRxSpendPerEmployee: 'Avg Annual Rx Spend / Employee ($)',
    savingsPercentage: 'Savings Percentage',
    employerSharePercentage: 'Employer Share Percentage',
    absenteeismReductionDaysPerEmployee: 'Absenteeism Reduction (Days / Employee)',
    avgDailyLaborCost: 'Avg Daily Labor Cost ($)',
    roiMultiplier: 'ROI Multiplier',
  };
  return map[key] || key.replace(/([A-Z])/g, ' $1').replace(/^./, (s) => s.toUpperCase());
}

function ImpactBox({ label, value, color, bg }) {
  return (
    <div style={{ background: bg, padding: '10px 12px', borderRadius: 8 }}>
      <div style={{ fontSize: 9, fontWeight: 700, color: C.textLight, letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 2 }}>
        {label}
      </div>
      <div style={{ ...S.mono, fontSize: 16, fontWeight: 800, color }}>
        {fmt(value)}
      </div>
    </div>
  );
}
